import { useState } from "react"
import { IPolicyCancellationReason } from "contexts/opus/context"
import {
  Button,
  Descriptions,
  Form,
  Modal,
  Select,
  Spin,
  Row,
  Popconfirm,
  DatePicker,
  Col,
  Checkbox,
  Alert
} from "antd"
import styled from "styled-components"
import { Moment } from "moment"
import { IMoney } from "platform-client/types"
import { PaymentPlanType } from "platform-client/types"
import { MoneyDisplay } from "components/display/MoneyDisplay"
import { IAdditionalProduct } from "models/policies/additionalProduct"
import { BrokerBackdateNote } from "containers/policies/views/PolicyDetails/v2_components/broker-backdate-note"
import {
  brokerBackdateLimitInfoMessage,
  brokerBackdateLimitRestrictsDates,
  brokerNoSelectableDateMessage,
  brokerHasNoSelectableCancellationDate,
  isCancellationDateDisabled
} from "utils/pocketknife/cancellation-date"

const StyledRow = styled(Row)`
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const StyledCol = styled(Col)`
  & > * {
    margin-bottom: 8px;
    :last-child {
      margin-bottom: 0;
    }
  }
`

const StyledMoneyDescriptions = styled(Descriptions)`
  .ant-descriptions-item.total .ant-descriptions-item-label,
  .ant-descriptions-item.total .ant-descriptions-item-content {
    font-weight: bold;
  }

  .ant-descriptions-item-content {
    justify-content: flex-end;
  }
`

const Component: React.FC<IPolicyCancellation> = ({
  policyRef,
  refundAmount,
  totalClearedPayments,
  totalRefundsIssued,
  availableRefund,
  onCancel,
  onChangeReason,
  cancellationReason,
  cancellationReasons,
  isButtonLoading,
  isLayoutLoading,
  setEffectiveCancellationDate,
  effectiveCancellationDate,
  onProRataRefundChange,
  proRataRefund,
  fetchCancellationInfo,
  coverRange,
  minCancellationDate,
  brokerMaxBackdateDays,
  paymentPlanType,
  additionalProducts,
  onAdditionalProductsChange,
  additionalProductsToCancel
}) => {
  const [visible, setVisible] = useState<boolean>(false)

  const noSelectableDate = brokerHasNoSelectableCancellationDate(
    coverRange?.[1],
    brokerMaxBackdateDays
  )

  const handleCancel = () => {
    setVisible(false)
  }

  const additionalProductOptions =
    additionalProducts
      ?.filter((ap) => ap.cancelledOnLocal === null)
      .map((ap) => ({ label: ap.name, value: ap.referenceID })) ?? []

  return (
    <>
      <Button
        loading={isButtonLoading}
        onClick={() => {
          fetchCancellationInfo(
            cancellationReason,
            effectiveCancellationDate,
            proRataRefund,
            additionalProductsToCancel
          )
          setVisible(true)
        }}
      >
        Cancel Policy
      </Button>
      <Modal
        visible={visible}
        onCancel={handleCancel}
        footer={
          <Row justify="space-between">
            <Button onClick={handleCancel}>Exit</Button>
            <Popconfirm
              title="Are you sure you wish to cancel your Policy"
              disabled={noSelectableDate}
              okButtonProps={{
                htmlType: "submit",
                form: "policyCancellationForm"
              }}
            >
              <Button
                type="primary"
                htmlType="submit"
                form="policyCancellationForm"
                disabled={isLayoutLoading || noSelectableDate}
              >
                Cancel Policy
              </Button>
            </Popconfirm>
          </Row>
        }
      >
        <Spin spinning={isLayoutLoading}>
          <Form
            layout="vertical"
            id="policyCancellationForm"
            onFinish={onCancel}
          >
            <Form.Item
              name="cancellation_reason"
              label="Cancellation reason:"
              rules={[
                {
                  required: true,
                  message: "Please select cancellation reason"
                }
              ]}
            >
              <Select
                placeholder="Select cancellation reason"
                value={cancellationReason?.referenceID}
                onChange={(referenceID) => {
                  const newReason = cancellationReasons?.find(
                    (reason) => reason.referenceID === referenceID
                  )
                  onChangeReason(newReason as IPolicyCancellationReason)
                }}
                options={cancellationReasons.map((reason) => ({
                  label: reason.text,
                  value: reason.referenceID
                }))}
              />
            </Form.Item>
            <Row gutter={24}>
              <Col span={availableRefund != null ? 12 : 24}>
                <Descriptions
                  size="small"
                  column={1}
                  style={{ margin: "20px 0 0" }}
                >
                  <Descriptions.Item label="Policy Reference">
                    {policyRef}
                  </Descriptions.Item>
                  <Descriptions.Item label="Cover period start">
                    {coverRange ? coverRange[0].format("DD/MM/YYYY") : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Cover period end">
                    {coverRange ? coverRange[1].format("DD/MM/YYYY") : "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              {availableRefund != null && (
                <Col span={12}>
                  <StyledMoneyDescriptions
                    size="small"
                    column={1}
                    style={{ margin: "20px 0 0" }}
                  >
                    <Descriptions.Item label="Total Cleared Payments">
                      <MoneyDisplay value={totalClearedPayments} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Total Refunds Issued">
                      <MoneyDisplay value={totalRefundsIssued} />
                    </Descriptions.Item>
                    <Descriptions.Item
                      className="total"
                      label="Available Refund"
                    >
                      <MoneyDisplay value={availableRefund} />
                    </Descriptions.Item>
                  </StyledMoneyDescriptions>
                </Col>
              )}
            </Row>
            <hr />
            <Descriptions size="small" column={1}>
              <Descriptions.Item
                label="Refund Amount"
                contentStyle={{ justifyContent: "flex-end" }}
              >
                {refundAmount === undefined ? (
                  "-"
                ) : (
                  <MoneyDisplay value={refundAmount} />
                )}
              </Descriptions.Item>
            </Descriptions>
            <StyledCol>
              <StyledRow>
                Effective Cancellation Date:
                <DatePicker
                  disabledDate={(current) =>
                    isCancellationDateDisabled(current, {
                      minCancellationDate,
                      coverEndDate: coverRange?.[1],
                      brokerMaxBackdateDays
                    })
                  }
                  id="effectiveCancellationDate"
                  format="DD/MM/YYYY"
                  allowClear={false}
                  value={effectiveCancellationDate}
                  onChange={(date) =>
                    setEffectiveCancellationDate(date as Moment)
                  }
                />
              </StyledRow>
              {brokerMaxBackdateDays !== undefined &&
                (noSelectableDate ? (
                  <Alert
                    type="warning"
                    message={brokerNoSelectableDateMessage(
                      brokerMaxBackdateDays
                    )}
                  />
                ) : (
                  brokerBackdateLimitRestrictsDates(
                    minCancellationDate,
                    brokerMaxBackdateDays
                  ) && (
                    <BrokerBackdateNote>
                      {brokerBackdateLimitInfoMessage(brokerMaxBackdateDays)}
                    </BrokerBackdateNote>
                  )
                ))}
              {paymentPlanType === "AnnualMonthlySubscription" && (
                <StyledRow>
                  Pro Rata Customer Refund:
                  <Checkbox
                    id="prorataOverride"
                    onChange={(event) =>
                      onProRataRefundChange(event.target.checked as boolean)
                    }
                    checked={proRataRefund}
                  />
                </StyledRow>
              )}
              {additionalProductOptions.length > 0 && (
                <Checkbox.Group
                  onChange={(event) =>
                    onAdditionalProductsChange(event as string[])
                  }
                  value={additionalProductsToCancel}
                  style={{ width: "100%" }}
                >
                  <StyledRow>
                    {additionalProductOptions.map((ap) => (
                      <>
                        {ap.label}
                        <Checkbox value={ap.value} />
                      </>
                    ))}
                  </StyledRow>
                </Checkbox.Group>
              )}
            </StyledCol>
          </Form>
        </Spin>
      </Modal>
    </>
  )
}

export default Component

interface IPolicyCancellation {
  policyRef: string
  refundAmount?: IMoney
  totalClearedPayments?: IMoney | null
  totalRefundsIssued?: IMoney | null
  availableRefund?: IMoney | null
  onCancel: () => void
  onChangeReason: (reason: IPolicyCancellationReason) => void
  cancellationReason: IPolicyCancellationReason | null | undefined
  cancellationReasons: IPolicyCancellationReason[]
  isButtonLoading: boolean
  isLayoutLoading: boolean
  setEffectiveCancellationDate: (date: Moment) => void
  effectiveCancellationDate: Moment
  onProRataRefundChange: (prorata: boolean) => void
  proRataRefund: boolean
  fetchCancellationInfo: (
    reason: IPolicyCancellationReason | null | undefined,
    date: Moment | null | undefined,
    proRataRefund: boolean | null | undefined,
    additionalProductsToCancel: string[] | null | undefined
  ) => void
  coverRange?: Moment[]
  minCancellationDate?: Moment
  // Undefined when the user isn't a broker, so isn't restricted
  brokerMaxBackdateDays?: number
  paymentPlanType?: PaymentPlanType
  additionalProducts?: IAdditionalProduct[]
  onAdditionalProductsChange: (selected: string[]) => void
  additionalProductsToCancel: string[]
}
